import { ComponentPropsWithoutRef, ReactElement } from 'react';

import styled from 'styled-components';

import { IconCloseSmall } from '@components/icons/close-small';
import { IconDirectionDown } from '@components/icons/direction-down';
import { Tooltip, TooltipProps } from '@components/tooltip';
import { useIsOverflowing } from '@hooks/use-is-overflowing';
import { Color, theme } from '@styles/theme';
import { classNames } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export type BadgeSize = 'sm' | 'md';

export type BadgeVariant =
  | 'neutral'
  | 'blue'
  | 'green'
  | 'red'
  | 'orange'
  | 'purple'
  | 'indigo'
  | 'teal';

export type BadgeProps = Omit<ComponentPropsWithoutRef<'div'>, 'color'> & {
  text?: string | number;
  variant?: BadgeVariant;
  isSolid?: boolean;
  size?: BadgeSize;
  icon?: ReactElement;
  hasChevron?: boolean;
  onRemove?: () => void;
  isRounded?: boolean;
  isInteractive?: boolean;
  tooltip?: string | Omit<TooltipProps, 'children'>;
  onClick?: () => void;
  color?: Color;
  bg?: Color;
  borderColor?: Color;
};

export const Badge = ({
  text,
  variant = 'neutral',
  isSolid = false,
  size = 'sm',
  icon,
  hasChevron = false,
  onRemove,
  isRounded = false,
  isInteractive = false,
  tooltip,
  onClick,
  color: colorOverride,
  bg: bgOverride,
  borderColor: borderColorOverride,
  className,
  ...rest
}: BadgeProps) => {
  const colors = (isSolid ? SOLID_VARIANT_COLORS : VARIANT_COLORS)[variant];
  const [textRef, isTextTruncated] = useIsOverflowing();

  const finalBg = bgOverride ?? colors.bg;
  const finalColor = colorOverride ?? colors.color;
  const finalBorderColor = borderColorOverride ?? colors.borderColor;
  const hasRemove = Boolean(onRemove);
  const finalTooltip = tooltip ?? (isTextTruncated ? String(text) : undefined);

  const content = (
    <BadgeContainer
      $size={size}
      $color={finalColor}
      $bg={finalBg}
      $borderColor={finalBorderColor}
      $rounded={isRounded}
      $isInteractive={isInteractive}
      $hasRemove={hasRemove}
      $hoverBg={colors.hoverBg}
      $hoverColor={colors.hoverColor}
      $hoverBorderColor={colors.hoverBorderColor}
      onClick={onClick}
      data-variant={variant}
      className={classNames('badge', className)}
      {...rest}
    >
      {icon && <IconWrapper $size={size}>{icon}</IconWrapper>}
      {text !== undefined && <BadgeText ref={textRef}>{text}</BadgeText>}
      {hasChevron && (
        <ChevronWrapper $size={size}>
          <IconDirectionDown size={16} />
        </ChevronWrapper>
      )}
      {onRemove && (
        <RemoveButton
          type="button"
          aria-label="Remove"
          $size={size}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
        >
          <IconCloseSmall />
        </RemoveButton>
      )}
    </BadgeContainer>
  );

  if (!finalTooltip) return content;

  if (typeof finalTooltip === 'string') {
    return <Tooltip content={finalTooltip}>{content}</Tooltip>;
  }

  return <Tooltip {...finalTooltip}>{content}</Tooltip>;
};

type ResolvedColors = {
  bg: Color;
  color: Color;
  borderColor?: Color;
  hoverBg?: Color;
  hoverColor?: Color;
  hoverBorderColor?: Color;
};

const VARIANT_COLORS: Record<BadgeVariant, ResolvedColors> = {
  neutral: { bg: 'uiTrans5', color: 'textHelper', hoverBg: 'uiTrans10' },
  blue: { bg: 'blueTrans10', color: 'elementBlue200', hoverBg: 'blueTrans20' },
  green: { bg: 'greenTrans10', color: 'elementGreen200', hoverBg: 'greenTrans20' },
  red: { bg: 'redTrans10', color: 'elementRed200', hoverBg: 'redTrans20' },
  orange: { bg: 'orangeTrans10', color: 'elementOrange200', hoverBg: 'orangeTrans20' },
  purple: { bg: 'uiTrans10', color: 'textPrimary', hoverBg: 'uiTrans20' },
  indigo: { bg: 'indigoTrans10', color: 'elementIndigo100', hoverBg: 'indigoTrans20' },
  teal: { bg: 'tealTrans10', color: 'elementTeal200', hoverBg: 'tealTrans20' },
};

const SOLID_VARIANT_COLORS: Record<BadgeVariant, ResolvedColors> = {
  neutral: VARIANT_COLORS.neutral,
  blue: { bg: 'elementBlue100', color: 'white100', hoverBg: 'elementBlue200' },
  green: { bg: 'elementGreen100', color: 'white100', hoverBg: 'elementGreen200' },
  red: { bg: 'elementRed100', color: 'white100', hoverBg: 'elementRed200' },
  orange: { bg: 'elementOrange100', color: 'white100', hoverBg: 'elementOrange200' },
  purple: { bg: 'elementPurple100', color: 'white100', hoverBg: 'elementPurple80' },
  indigo: { bg: 'elementIndigo100', color: 'white100', hoverBg: 'elementIndigo200' },
  teal: { bg: 'elementTeal100', color: 'white100', hoverBg: 'elementTeal200' },
};

const SIZE_MAP: Record<BadgeSize, string> = {
  sm: pxToRem(16),
  md: pxToRem(24),
};

const RADIUS_MAP: Record<BadgeSize, string> = {
  sm: pxToRem(2),
  md: pxToRem(4),
};

const FONT_MAP: Record<BadgeSize, string> = {
  sm: theme.font.semibold10,
  md: theme.font.semibold12,
};

const BadgeContainer = styled.div<{
  $size: BadgeSize;
  $color: Color;
  $bg: Color;
  $borderColor?: Color;
  $rounded: boolean;
  $isInteractive: boolean;
  $hasRemove: boolean;
  $hoverBg?: Color;
  $hoverColor?: Color;
  $hoverBorderColor?: Color;
}>((p) => ({
  flexShrink: 0,
  boxSizing: 'border-box',
  paddingInline: pxToRem(4),
  overflow: 'hidden',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: pxToRem(4),
  minWidth: SIZE_MAP[p.$size],
  maxWidth: '100%',
  height: SIZE_MAP[p.$size],
  color: theme.color[p.$color],
  background: theme.color[p.$bg],
  borderRadius: p.$rounded ? pxToRem(9999) : RADIUS_MAP[p.$size],
  font: FONT_MAP[p.$size],
  whiteSpace: 'nowrap' as const,
  transition: transitionStandard('background', 'color', 'border-color'),
  cursor: p.$isInteractive ? 'pointer' : undefined,
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  borderColor: p.$borderColor ? theme.color[p.$borderColor] : 'transparent',

  ...(p.$isInteractive &&
    (p.$hoverBg || p.$hoverColor) && {
      '&:hover': {
        ...(p.$hoverBg && { background: theme.color[p.$hoverBg] }),
        ...(p.$hoverColor && { color: theme.color[p.$hoverColor] }),
        ...(p.$hoverBorderColor && { borderColor: theme.color[p.$hoverBorderColor] }),
      },
    }),

  ...(p.$hasRemove && {
    paddingRight: pxToRem(2),
  }),
}));

const BadgeText = styled.span({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const IconWrapper = styled.span<{ $size: BadgeSize }>((p) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: p.$size === 'sm' ? pxToRem(12) : pxToRem(16),
  height: p.$size === 'sm' ? pxToRem(12) : pxToRem(16),
  svg: {
    width: '100%',
    height: '100%',
  },
}));

const ChevronWrapper = styled(IconWrapper)({
  marginLeft: pxToRem(-2),
});

const RemoveButton = styled.button<{ $size: BadgeSize }>((p) => ({
  all: 'unset',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  color: 'inherit',
  width: p.$size === 'sm' ? pxToRem(12) : pxToRem(16),
  height: p.$size === 'sm' ? pxToRem(12) : pxToRem(16),
  svg: {
    width: '100%',
    height: '100%',
  },
}));
